import { z } from "zod";
import { http, HttpResponse, type HttpHandler } from "msw";
import { API_PREFIX, type PaginationMeta } from "@nasam/kernel";
import type {
  Subscription,
  SubscriptionDetailModule,
  SubscriptionModule,
  SubscriptionModuleRef,
} from "../domain";
import {
  deriveSubscriptionStatus,
  subscriptionAddModulesWriteDtoSchema,
  subscriptionDeviceAllocationWriteDtoSchema,
  subscriptionWriteDtoSchema,
} from "../data";
import {
  deviceModelFixtures,
  deviceTypeFixtures,
  subscriptionFixtures,
} from "./subscriptionFixtures";

const SUBSCRIPTIONS_PATH = `${API_PREFIX}/subscriptions`;

// A sale always goes through; its `usageInfo` says where the modules sold
// now stand. `module-07` is the one the catalog fixtures have no companies
// left of, so the form warns before selling it; `module-06` has room in the
// catalog but comes back over -- the last company went meanwhile -- so the
// form tells after.
const FULL_MODULE_ID = "module-07";
const RACED_MODULE_ID = "module-06";

function usageInfoFor(moduleIds: readonly string[]) {
  return {
    modules: moduleIds.map((moduleId) =>
      moduleId === FULL_MODULE_ID
        ? { moduleId, quantity: 5, used: 6, remaining: 0, overage: 1 }
        : moduleId === RACED_MODULE_ID
          ? { moduleId, quantity: 16, used: 17, remaining: 0, overage: 1 }
          : { moduleId, quantity: 20, used: 1, remaining: 19, overage: 0 },
    ),
  };
}

// A handful of modules the caller holds but hasn't sold any subscriber yet --
// self-contained rather than cross-imported from the catalog feature, to
// respect the module-boundary rule outside test files.
const ASSIGNABLE_MODULE_FIXTURES: readonly SubscriptionModuleRef[] = [
  {
    id: "module-29",
    slug: "module-29",
    nameEn: "Module 29",
    nameAr: "وحدة 29",
    descriptionEn: "Sellable module 29.",
    descriptionAr: "الوحدة القابلة للبيع رقم 29.",
  },
  {
    id: "module-30",
    slug: "module-30",
    nameEn: "Module 30",
    nameAr: "وحدة 30",
    descriptionEn: "Sellable module 30.",
    descriptionAr: "الوحدة القابلة للبيع رقم 30.",
  },
];

export interface SubscriptionHandlerOptions {
  readonly apiBaseUrl: string;
}
function positive(value: string | null, fallback: number): number {
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : fallback;
}
function meta(
  url: URL,
  page: number,
  limit: number,
  totalCount: number,
): PaginationMeta {
  const totalPages = Math.ceil(totalCount / limit);
  const pageUrl = (targetPage: number) => {
    const target = new URL(url);
    target.searchParams.set("page", String(targetPage));
    target.searchParams.set("limit", String(limit));
    return target.toString();
  };
  return {
    totalCount,
    currentPage: page,
    totalPages,
    firstPageUrl: pageUrl(1),
    lastPageUrl: pageUrl(Math.max(1, totalPages)),
    nextPageUrl: page < totalPages ? pageUrl(page + 1) : null,
    previousPageUrl: page > 1 ? pageUrl(page - 1) : null,
  };
}
function filtered(
  items: readonly Subscription[],
  url: URL,
): readonly Subscription[] {
  const params = url.searchParams;
  const search = params.get("search")?.toLocaleLowerCase();
  const subscriberId = params.get("subscriberId");
  const moduleId = params.get("moduleId");
  const activeOnly = params.get("activeOnly") === "true";
  const status = params.get("status");
  const fromCreatedAt = params.get("fromCreatedAt");
  const toCreatedAt = params.get("toCreatedAt");
  const sortBy = params.get("sortBy") ?? "CREATED_AT";
  const direction = params.get("sort") === "desc" ? -1 : 1;
  return items
    .filter(
      (item) =>
        search === undefined ||
        item.subscriber.nameEn.toLocaleLowerCase().includes(search) ||
        item.subscriber.nameAr.includes(search),
    )
    .filter(
      (item) => subscriberId === null || item.subscriberId === subscriberId,
    )
    .filter(
      (item) =>
        moduleId === null ||
        item.modules.some((module) => module.moduleId === moduleId),
    )
    .filter((item) => !activeOnly || item.status === "ACTIVE")
    .filter((item) => status === null || item.status === status)
    .filter(
      (item) =>
        fromCreatedAt === null ||
        Date.parse(item.createdAt) >= Date.parse(fromCreatedAt),
    )
    .filter(
      (item) =>
        toCreatedAt === null ||
        Date.parse(item.createdAt) <= Date.parse(toCreatedAt),
    )
    .toSorted((left, right) => {
      const value = (subscription: Subscription): string | number => {
        switch (sortBy) {
          case "COMPANY":
            return subscription.subscriber.nameEn;
          case "TOTAL_PRICE":
            return subscription.totalSubscriptionPrice;
          case "MODULES":
            return subscription.modules.length;
          default:
            return subscription.createdAt;
        }
      };
      const a = value(left);
      const b = value(right);
      const result =
        typeof a === "number" && typeof b === "number"
          ? a - b
          : String(a).localeCompare(String(b));
      return result * direction;
    });
}

/** Mirrors the backend deriving a line's own status from its date range. */
function deriveModuleStatus(
  startDate: string | null,
  endDate: string | null,
): "ACTIVE" | "SCHEDULED" | "EXPIRED" {
  if (startDate === null || endDate === null) return "ACTIVE"; // "No Period"
  const now = Date.now();
  if (new Date(startDate).getTime() > now) return "SCHEDULED";
  if (new Date(endDate).getTime() < now) return "EXPIRED";
  return "ACTIVE";
}

/** The real details endpoint returns `assignableModules` in the same line shape
 * as `current` (docs/api/nasam-api.md), but as a not-yet-persisted line -- the
 * top-level `id` comes back as an empty string. Mirror that exactly. */
function toAssignableLine(
  ref: SubscriptionModuleRef,
): SubscriptionDetailModule {
  return {
    id: "",
    moduleId: ref.id,
    module: ref,
    purchasingPrice: 0,
    startDate: null,
    endDate: null,
    status: "ACTIVE",
    periodDays: null,
    remainingDays: null,
  };
}

/** Buckets a flat modules[] the way the real detail response arrives pre-bucketed. */
function toDetailModule(module: SubscriptionModule): SubscriptionDetailModule {
  if (module.startDate === null || module.endDate === null) {
    return { ...module, periodDays: null, remainingDays: null };
  }
  const start = new Date(module.startDate).getTime();
  const end = new Date(module.endDate).getTime();
  const now = Date.now();
  const periodDays = Math.round((end - start) / 86_400_000);
  const remainingDays = Math.max(0, Math.round((end - now) / 86_400_000));
  return { ...module, periodDays, remainingDays };
}

export function createSubscriptionHandlers({
  apiBaseUrl,
}: SubscriptionHandlerOptions): readonly HttpHandler[] {
  let subscriptions: Subscription[] = subscriptionFixtures.map((item) => ({
    ...item,
    modules: item.modules.map((module) => ({ ...module })),
    devices: item.devices.map((device) => ({ ...device })),
  }));

  return [
    http.get(`${apiBaseUrl}${SUBSCRIPTIONS_PATH}`, ({ request }) => {
      const url = new URL(request.url);
      const values = filtered(subscriptions, url);
      const page = positive(url.searchParams.get("page"), 1);
      const limit = positive(url.searchParams.get("limit"), 10);
      return HttpResponse.json({
        data: values.slice((page - 1) * limit, page * limit),
        meta: meta(url, page, limit, values.length),
      });
    }),
    http.get(`${apiBaseUrl}${SUBSCRIPTIONS_PATH}/device-types`, () =>
      HttpResponse.json({ data: deviceTypeFixtures }),
    ),
    http.get(
      `${apiBaseUrl}${SUBSCRIPTIONS_PATH}/device-types/:deviceTypeId/models`,
      ({ params }) => {
        const models = deviceModelFixtures.filter(
          (item) => item.deviceTypeId === params.deviceTypeId,
        );
        return HttpResponse.json({ data: models });
      },
    ),
    http.get(`${apiBaseUrl}${SUBSCRIPTIONS_PATH}/:id`, ({ params }) => {
      const found = subscriptions.find((item) => item.id === params.id);
      if (found === undefined) {
        return HttpResponse.json(
          { statusCode: 404, path: SUBSCRIPTIONS_PATH, message: "Not found" },
          { status: 404 },
        );
      }
      const detailModules = found.modules.map(toDetailModule);
      return HttpResponse.json({
        data: {
          id: found.id,
          subscriber: found.subscriber,
          status: found.status,
          totalSubscriptionPrice: found.totalSubscriptionPrice,
          current: detailModules.filter(
            (m) => m.status === "ACTIVE" || m.status === "INACTIVE",
          ),
          history: detailModules.filter((m) => m.status === "EXPIRED"),
          future: detailModules.filter((m) => m.status === "SCHEDULED"),
          assignableModules: ASSIGNABLE_MODULE_FIXTURES.map(toAssignableLine),
          devices: found.devices,
        },
      });
    }),
    http.post(`${apiBaseUrl}${SUBSCRIPTIONS_PATH}`, async ({ request }) => {
      const body = subscriptionWriteDtoSchema.parse(await request.json());
      const id = `subscription-${String(subscriptions.length + 1).padStart(2, "0")}`;
      const subscriber = subscriptionFixtures.find(
        (item) => item.subscriberId === body.subscriberId,
      )?.subscriber ?? {
        id: body.subscriberId,
        nameEn: body.subscriberId,
        nameAr: body.subscriberId,
      };
      const modules: SubscriptionModule[] = body.modules.map(
        (module, index) => ({
          id: `${id}-module-${index + 1}`,
          moduleId: module.moduleId,
          module: {
            id: module.moduleId,
            slug: module.moduleId,
            nameEn: module.moduleId,
            nameAr: module.moduleId,
            descriptionEn: `Sellable module ${module.moduleId}.`,
            descriptionAr: `الوحدة القابلة للبيع ${module.moduleId}.`,
          },
          purchasingPrice: module.purchasingPrice ?? 0,
          startDate: module.startDate,
          endDate: module.endDate,
          status: deriveModuleStatus(module.startDate, module.endDate),
        }),
      );
      const created: Subscription = {
        id,
        subscriberId: body.subscriberId,
        subscriber,
        modules,
        devices: [],
        totalSubscriptionPrice: modules.reduce(
          (sum, module) => sum + module.purchasingPrice,
          0,
        ),
        status: deriveSubscriptionStatus(modules),
        createdAt: "2026-03-01T09:00:00.000Z",
        updatedAt: "2026-03-01T09:00:00.000Z",
      };
      subscriptions = [...subscriptions, created];
      return HttpResponse.json(
        {
          data: {
            ...created,
            usageInfo: usageInfoFor(body.modules.map((m) => m.moduleId)),
          },
          message: "Created successfully",
        },
        { status: 201 },
      );
    }),
    http.post(
      `${apiBaseUrl}${SUBSCRIPTIONS_PATH}/:id/modules`,
      async ({ params, request }) => {
        const body = subscriptionAddModulesWriteDtoSchema.parse(
          await request.json(),
        );
        const index = subscriptions.findIndex((item) => item.id === params.id);
        if (index < 0) {
          return HttpResponse.json(
            { statusCode: 404, path: SUBSCRIPTIONS_PATH, message: "Not found" },
            { status: 404 },
          );
        }
        const current = subscriptions[index] as Subscription;
        const newModules: SubscriptionModule[] = body.modules.map(
          (module, moduleIndex) => ({
            id: `${current.id}-module-${current.modules.length + moduleIndex + 1}`,
            moduleId: module.moduleId,
            module: {
              id: module.moduleId,
              slug: module.moduleId,
              nameEn: module.moduleId,
              nameAr: module.moduleId,
              descriptionEn: `Sellable module ${module.moduleId}.`,
              descriptionAr: `الوحدة القابلة للبيع ${module.moduleId}.`,
            },
            purchasingPrice: module.purchasingPrice ?? 0,
            startDate: module.startDate,
            endDate: module.endDate,
            status: deriveModuleStatus(module.startDate, module.endDate),
          }),
        );
        const modules = [...current.modules, ...newModules];
        const updated: Subscription = {
          ...current,
          modules,
          totalSubscriptionPrice: modules.reduce(
            (sum, module) => sum + module.purchasingPrice,
            0,
          ),
          status: deriveSubscriptionStatus(modules),
          updatedAt: "2026-03-02T09:00:00.000Z",
        };
        subscriptions = subscriptions.with(index, updated);
        return HttpResponse.json(
          {
            data: {
              ...updated,
              usageInfo: usageInfoFor(body.modules.map((m) => m.moduleId)),
            },
          },
          { status: 201 },
        );
      },
    ),

    // Pending-contract (see docs/api/gaps.md) below this line.
    http.post(
      `${apiBaseUrl}${SUBSCRIPTIONS_PATH}/:id/devices`,
      async ({ params, request }) => {
        const body = z
          .array(subscriptionDeviceAllocationWriteDtoSchema)
          .parse(((await request.json()) as { devices: unknown }).devices);
        const index = subscriptions.findIndex((item) => item.id === params.id);
        if (index < 0) {
          return HttpResponse.json(
            { statusCode: 404, path: SUBSCRIPTIONS_PATH, message: "Not found" },
            { status: 404 },
          );
        }
        const current = subscriptions[index] as Subscription;
        const updated: Subscription = {
          ...current,
          devices: [
            ...current.devices,
            ...body.map((device, deviceIndex) => {
              const type = deviceTypeFixtures.find(
                (t) => t.id === device.deviceTypeId,
              );
              const model = deviceModelFixtures.find(
                (m) => m.id === device.deviceModelId,
              );
              return {
                id: `${current.id}-device-${current.devices.length + deviceIndex + 1}`,
                deviceTypeId: device.deviceTypeId,
                deviceTypeNameEn: type?.nameEn ?? device.deviceTypeId,
                deviceTypeNameAr: type?.nameAr ?? device.deviceTypeId,
                deviceModelId: device.deviceModelId,
                deviceModelNameEn: model?.nameEn ?? device.deviceModelId,
                deviceModelNameAr: model?.nameAr ?? device.deviceModelId,
                quantity: device.quantity,
                duration: device.duration,
              };
            }),
          ],
          updatedAt: "2026-03-02T09:00:00.000Z",
        };
        subscriptions = subscriptions.with(index, updated);
        return HttpResponse.json({ data: updated });
      },
    ),
  ];
}
